/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useEffect, useCallback } from "react";

import {
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import { useReactToPrint } from 'react-to-print';
import msgNotificacion from '../../scripts/notification';
import LogoFS from '../../assets/images/logoFS.png';
import Formatter from "../../scripts/formatters";
import '../../styles/solicitudes.css';

const FormHorasExtrasConsolidadoPdf = ({ data, print, setPrint, filtros }) => {
  const componentRef = useRef(null);

  // Función para formatear horas a AM/PM
  const formatTime = (timeValue) => {
    if (!timeValue) return '';
    
    try {
      // Si viene como string de hora (HH:MM:SS), convertirlo
      if (typeof timeValue === 'string' && timeValue.includes(':') && !timeValue.includes('T')) {
        const [hours, minutes] = timeValue.split(':');
        const hour = parseInt(hours, 10);
        const min = parseInt(minutes, 10);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${min.toString().padStart(2, '0')} ${ampm}`;
      }
      
      // Si viene como string ISO (1970-01-01T17:00:00.000Z), extraer solo la hora
      if (typeof timeValue === 'string' && timeValue.includes('T')) {
        const timePart = timeValue.split('T')[1];
        if (timePart) {
          const timeWithoutMs = timePart.split('.')[0]; // Remover milisegundos
          const [hours, minutes] = timeWithoutMs.split(':');
          const hour = parseInt(hours, 10);
          const min = parseInt(minutes, 10);
          const ampm = hour >= 12 ? 'PM' : 'AM';
          const displayHour = hour % 12 || 12;
          return `${displayHour}:${min.toString().padStart(2, '0')} ${ampm}`;
        }
      }
      
      // Si viene como Date, extraer solo la hora
      const date = new Date(timeValue);
      if (!isNaN(date.getTime())) {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHour = hours % 12 || 12;
        return `${displayHour}:${minutes.toString().padStart(2, '0')} ${ampm}`;
      }
      
      return timeValue; // Fallback
    } catch (error) {
      return timeValue; // Fallback
    }
  };

  // useCallback para memorizar funciones y evitar re-renderizaciones innecesarias.
  const handleAfterPrint = useCallback(() => {
    msgNotificacion('Impresión exitosa', 'success', 500);
    setPrint(false);
  }, [setPrint]);

  const handleBeforeContent = useCallback(() => {
    msgNotificacion('Imprimiendo...', 'info', 1000);
  }, []);

  const handlePrint = useReactToPrint({
    documentTitle: 'REPORTE CONSOLIDADO DE HORAS EXTRAS',
    content: () => componentRef.current,
    onAfterPrint: handleAfterPrint,
    onBeforeGetContent: handleBeforeContent,
  });

  // Utilice una función separada para la lógica de impresión real para mejorar la legibilidad
  const triggerPrint = useCallback(() => {
    if (componentRef.current) {
      setTimeout(() => {
        handlePrint()
      }, 500)
    }
  }, [handlePrint]);

  useEffect(() => {
    if (print) {
      triggerPrint()
    }
  }, [print, triggerPrint]);

  return (    
    <div style={{ padding: 1, margin: 1 }} className='no-print'>
      <div ref={componentRef} className='print-container'>
        <div className='vh-100 d-flex flex-column align-items-center'>
          <div className='d-flex align-items-center justify-content-center my-1 py-4'>
            <img src={LogoFS} alt='Logo contrata' height={110} width={240} className='me-4' />

            <h4 className='text-center fw-bolder'>
              <span>REPORTE CONSOLIDADO DE HORAS EXTRAS</span>
              {data && data.length > 0 && (
                <p className='no-solicitud'>
                  <strong>Empleado:</strong> {data[0].NombreEmpleado?.toUpperCase()}
                </p>
              )}
              <p className='no-solicitud'>
                <strong>Período:</strong> {filtros?.fechaIni && filtros?.fechaFin ? 
                  `${Formatter.dateFormatter(filtros.fechaIni)} - ${Formatter.dateFormatter(filtros.fechaFin)}` : 
                  (data && data.length > 0 ? 
                    `${Formatter.dateFormatter(data[0].FechaTrabajo)} - ${Formatter.dateFormatter(data[data.length - 1].FechaTrabajo)}` : 
                    'Sin datos'
                  )
                }
              </p>
            </h4>
          </div>

          <TableContainer component={Paper} className='mt-3'>
            <Table size="small" aria-label="reporte consolidado">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>
                    FECHA
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>
                    ENTRADA
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>
                    SALIDA
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>
                    HORAS EXTRAS
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>
                    ESTADO
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>
                    MOTIVO
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell align="center" sx={{ border: '1px solid #ddd' }}>
                      {Formatter.dateFormatter(row.FechaTrabajo)}
                    </TableCell>
                    <TableCell align="center" sx={{ border: '1px solid #ddd' }}>
                      {row.entrada ? (
                        <div>
                          <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                            {formatTime(row.entrada.horaReal)}
                          </div>
                          {row.entrada.horasExtras > 0 && (
                            <div style={{ fontSize: '0.8rem', color: 'green' }}>
                              <strong>+{parseFloat(row.entrada.horasExtras).toFixed(2)}h</strong>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#999' }}>Sin hora extra</span>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ border: '1px solid #ddd' }}>
                      {row.salida ? (
                        <div>
                          <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                            {formatTime(row.salida.horaReal)}
                          </div>
                          {row.salida.horasExtras > 0 && (
                            <div style={{ fontSize: '0.8rem', color: 'green' }}>
                              <strong>+{parseFloat(row.salida.horasExtras).toFixed(2)}h</strong>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#999' }}>Sin hora extra</span>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ border: '1px solid #ddd', fontWeight: 'bold', color: 'green' }}>
                      {row.totalHorasExtras > 0 ? `${parseFloat(row.totalHorasExtras).toFixed(2)}h` : '0.00h'}
                    </TableCell>
                    <TableCell align="center" sx={{ border: '1px solid #ddd' }}>
                      {row.estado}
                    </TableCell>
                    <TableCell align="center" sx={{ border: '1px solid #ddd' }}>
                      {row.motivo || 'Sin motivo especificado'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Resumen al final */}
          <div className='mt-4 w-100'>
            <div className='d-flex justify-content-center mt-2'>
              <div style={{ display: 'flex', gap: '60px', alignItems: 'center' }}>
                <Typography>
                  <strong>Total de días reportados:</strong> {data?.length || 0}
                </Typography>
                <Typography>
                  <strong>Total de horas extras:</strong> {data?.reduce((total, row) => total + (parseFloat(row.totalHorasExtras) || 0), 0).toFixed(2)}h
                </Typography>
              </div>
            </div>
          </div>

          {/* Firmas */}
          <Grid container spacing={2} className='px-5 py-5 mt-4 border-top justify-content-between'>
            <Grid item xs={3} md={3} textAlign='center'>
              <Typography className='text-decoration-underline' fontSize={10}>
                {data && data.length > 0 ? data[0].NombreEmpleado : ''}
              </Typography>
              <Typography fontWeight='bolder'>Solicita</Typography>
            </Grid>
            <Grid item xs={3} md={3} textAlign='center'>
              <Typography className='text-decoration-underline' fontSize={10}>
                {data && data.length > 0 ? (data[0].JefeInmediato || '_________________') : '_________________'}
              </Typography>
              <Typography fontWeight='bolder'>Autoriza</Typography>
            </Grid>
            <Grid item xs={3} md={3} textAlign='center'>
              <Typography className='text-decoration-underline' fontSize={10}>
                {data && data.length > 0 ? (data[0].JefeRRHH || '_________________') : '_________________'}
              </Typography>
              <Typography fontWeight='bolder'>Recibe RRHH</Typography>
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};

export default FormHorasExtrasConsolidadoPdf;
